import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { EtatReseau, TerminalIdentityService } from '@stockflow/offline-core';
import { CheckoutPayload, CheckoutResult, DeferredCheckoutResult } from '../models/pos-catalog.model';
import { fromCents } from '../../shared/utils/money.utils';
import { PosDataService } from './pos-data.service';
import { CashRegisterService } from './cash-register.service';
import { VentesDifferees } from './ventes-differees.service';
import { OperationsEnCours } from './operations-en-cours';
import { CartStateService, lineTotalCentsOf } from './cart-state.service';

/**
 * Encaissement du panier : construction du payload, clé d'idempotence, envoi,
 * et mise en file hors ligne.
 *
 * Sorti de `CartService`, au-delà de la limite de 500 lignes. L'API publique
 * reste `CartService.checkout`, qui délègue ici.
 */
@Injectable({
    providedIn: 'root'
})
export class CartCheckoutService {
    private posData = inject(PosDataService);
    private state = inject(CartStateService);
    private cashRegisterService = inject(CashRegisterService);
    /** Identite du poste, partagee avec /frontend. Voir shared/offline-core. */
    private terminalIdentity = inject(TerminalIdentityService);
    private etatReseau = inject(EtatReseau);
    private ventes = inject(VentesDifferees);
    private operations = inject(OperationsEnCours);

    /**
     * Clé d'idempotence de la tentative de vente en cours.
     *
     * Conservée après un échec pour qu'un nouvel essai porte la même clé et ne
     * puisse pas créer une seconde vente. Remise à zéro après un succès, la
     * vente suivante devant obtenir une clé distincte.
     */
    private pendingCheckoutKey: string | null = null;

    /**
     * Clé d'idempotence de la vente en cours.
     *
     * Préfixée par l'identité du poste (écart Y4) : une clé anonyme est unique
     * mais intraçable. Lors d'un support, on doit pouvoir rattacher une vente
     * en échec à la caisse qui l'a saisie.
     *
     * Le repli précédent utilisait Math.random(), dont la collision n'est
     * qu'improbable. randomUUID l'exclut ; à défaut, l'identité du poste
     * garantit à elle seule qu'aucune autre caisse ne produira la même clé.
     */
    private nextCheckoutKey(): string {
        if (!this.pendingCheckoutKey) {
            const poste = this.terminalIdentity.exiger();
            const alea =
                typeof crypto !== 'undefined' && 'randomUUID' in crypto
                    ? crypto.randomUUID()
                    : `${Date.now()}-${this.compteurLocal++}`;
            this.pendingCheckoutKey = `${poste}-${alea}`;
        }
        return this.pendingCheckoutKey;
    }

    /** Compteur de repli, utilisé seulement si randomUUID est indisponible. */
    private compteurLocal = 0;

    /**
     * Finalise la vente : crée commande, facture, paiement côté backend.
     * Les montants sont envoyés en centimes (integer) pour éviter les erreurs d'arrondi.
     *
     * Une vente qui part ne se coupe pas : le verrou du poste attend qu'elle ait fini.
     */
    checkout(paymentMethodId: number, amountPaid: number): Promise<CheckoutResult | DeferredCheckoutResult> {
        return this.operations.pendant(() => this.executerCheckout(paymentMethodId, amountPaid));
    }

    private async executerCheckout(paymentMethodId: number, amountPaid: number): Promise<CheckoutResult | DeferredCheckoutResult> {
        const customer = this.state.selectedCustomer();
        const currency = this.posData.currency();
        const idempotencyKey = this.nextCheckoutKey();

        // Le backend recalcule le montant de la facture depuis ces lignes ; il
        // ignore totalAmount. lineTotalCents est donc transmis explicitement pour
        // que la facture corresponde au centime à ce que le client voit à l'écran.
        //
        // unitPrice reste un prix unitaire arrondi (colonne decimal(12,2)) et ne
        // peut pas porter la valeur exacte : 24 unités à 0.8333 s'écrivent 0.83,
        // soit 19.92 au lieu de 20.00. C'est lineTotal qui fait foi.
        const lines = this.state.items().map(item => {
            const multiplier = item.packaging.multiplier || 1;
            const quantity = item.qty * multiplier;
            const lineTotalCents = lineTotalCentsOf(item);
            const unitPriceCents = Math.round(lineTotalCents / quantity);
            const unitPrice = fromCents(unitPriceCents);
            return {
                productId: item.id,
                quantity,
                unitPrice,
                lineTotal: fromCents(lineTotalCents),
                variantId: item.packaging.variantId,
                unitId: item.packaging.unitId,
                familyId: item.familyId,
                isComposite: item.isComposite || false,
                recipeComponents: item.recipeComponents || undefined,
            };
        });

        // Note : le backend ignore totalAmount et refacture la somme des
        // lineTotal ci-dessus. Ce champ ne sert qu'au contrôle de cohérence.
        const totalAmount = this.state.total();

        // Include active cash session ID if available
        const activeSession = this.cashRegisterService.activeSession();
        const cashSessionId = activeSession?.id ? Number(activeSession.id) : undefined;

        const discountPct = this.state.discount();
        const payload: CheckoutPayload = {
            customerId: customer?.id || 0,
            currency: currency.code,
            totalAmount,
            // Montant réellement encaissé, transmis tel quel : c'est lui qui
            // détermine le CHANGE_GIVEN (monnaie rendue) du mouvement de caisse.
            // Le gonfler à totalAmount fabriquait de la fausse monnaie rendue
            // quand les deux référentiels divergeaient. Le statut de la facture
            // ne dépend pas de ce champ : le backend solde sur invoice.total.
            amountPaid,
            paymentMethodId,
            cashSessionId,
            idempotencyKey,
            ...(discountPct > 0 ? { discountPercent: discountPct } : {}),
            productNames: Object.fromEntries(this.state.items().map(i => [i.id, i.name])),
            lines
        };

        try {
            const result = await firstValueFrom(this.posData.checkout(payload));
            this.etatReseau.signalerSucces();
            // La vente est passée : la clé a rempli son rôle. La conserver ferait
            // rejouer ce résultat sur la vente suivante au lieu d'en créer une.
            // En cas d'échec on la garde, pour qu'un nouvel essai soit dédupliqué.
            this.pendingCheckoutKey = null;
            return result;
        } catch (erreur: unknown) {
            // Un refus metier — stock insuffisant, exercice clos — se
            // reproduira a l'identique : il remonte au vendeur. Seule une panne
            // reseau est mise en file. Voir shared/offline-core, ecart Y0.
            if (!this.etatReseau.estPanneReseau(erreur)) throw erreur;

            this.etatReseau.signalerEchecReseau();
            await this.ventes.mettreEnFile(idempotencyKey, payload);
            // La cle est CONSERVEE : c'est elle qui deduplique quand la file
            // sera drainee. La liberer ferait creer une seconde vente.
            // `mettreEnFile` rafraichit le compteur et est attendu : le signal est
            // a jour au retour de checkout().

            return {
                differee: true,
                idempotencyKey,
                message:
                    'Vente enregistree hors ligne. Elle sera transmise au retour du reseau.',
            };
        }
    }
}
